// disOS landing page — editorial / premium-minimal. Public (no auth). CTAs go to sign-in.
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePubBody, asset } from './pubBody';
import './public.css';

const Arrow = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

export function Landing() {
  usePubBody();
  const vid = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(true);
  const toggle = () => {
    const v = vid.current; if (!v) return;
    if (v.paused) { void v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  return (
    <div className="pub">
      <header className="nav">
        <div className="wrap">
          <Link className="brand" to="/"><span className="badge">dis</span><span className="wm">disOS</span></Link>
          <nav>
            <a className="link" href="#how">How it works</a>
            <Link className="link" to="/login">Sign in</Link>
            <Link className="btn sm" to="/login">Get started</Link>
          </nav>
        </div>
      </header>

      <section className="hero" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          <div className="eyebrow">Current California guideline · <b>SB 343</b></div>
          <h1>Child &amp; spousal support, <em>settled in minutes</em>.</h1>
          <p className="lede">disOS runs California's guideline support for you — enter the two parents' case and the number is there, ready to file.</p>
          <div className="actions">
            <Link className="btn" to="/login">Get started</Link>
            <Link className="signin" to="/login">Sign in <Arrow /></Link>
          </div>
          <div className="subnote">No install · runs in your browser</div>
        </div>

        <div className="showcase">
          <div className="wrap">
            <div className="frame">
              <video ref={vid} poster={asset('dis-demo.jpg')} muted playsInline autoPlay loop preload="metadata">
                <source src={asset('dis-demo.mp4')} type="video/mp4" />
              </video>
            </div>
            <div className="vidctl">
              <button type="button" onClick={toggle}>
                {playing
                  ? <><svg viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" /></svg>Pause</>
                  : <><svg viewBox="0 0 24 24"><path d="M7 4v16l13-8z" /></svg>Play</>}
              </button>
              <span>A real case, start to finish.</span>
            </div>
          </div>
        </div>
      </section>

      <section id="how">
        <div className="wrap">
          <div className="kicker">Why disOS</div>
          <div className="lead">The guideline, done the way the law defines it.</div>
          <div className="values">
            <div className="value">
              <h3>Always current</h3>
              <p>disOS stays on California's live guideline, including the 2024 SB 343 rules — so your numbers move with the law, not a year behind it.</p>
            </div>
            <div className="value">
              <h3>Real tax figures</h3>
              <p>Published IRS and FTB tables, filing statuses and payroll treatment — the actual numbers, never rough estimates.</p>
            </div>
            <div className="value">
              <h3>Ready to file</h3>
              <p>Every case ends in a clean, court-ready report with each figure laid out — print it, attach it, done.</p>
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: 'var(--wash)', borderTop: '1px solid var(--line-2)', borderBottom: '1px solid var(--line-2)' }}>
        <div className="wrap">
          <div className="split">
            <div className="copy">
              <div className="kicker">The output</div>
              <h2>A report you can hand to the court.</h2>
              <p>One click turns any case into findings a judicial officer can read at a glance — the amount, who pays, and the terms, with nothing to reformat.</p>
              <Link className="btn ghost" to="/login">Get started <Arrow /></Link>
            </div>
            <div className="shot">
              <img src={asset('dis-report.jpg')} alt="disOS guideline child support findings report" />
            </div>
          </div>
        </div>
      </section>

      <section className="closing">
        <div className="wrap">
          <h2>Run your first case in a minute.</h2>
          <p>Open a case, enter the numbers, and watch the support figure appear.</p>
          <Link className="btn" to="/login">Get started</Link>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="disc">
            <Link className="brand" to="/"><span className="badge">dis</span><span className="wm">disOS</span></Link>
          </div>
          <div className="lnks">
            <Link to="/login">Sign in</Link>
            <a href="/docs/">Docs</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
